import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { getCustomerPortalTenants } from '../api/customerPortalApi';
import { getCustomerPortalTenantModules } from '../api/mafertappModulesApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { useCustomerPortal } from '../portal/CustomerPortalContext';
import { formatModuleLabel, formatModuleSource } from '../utils/customerPortalLabels';
import { CustomerSetupRequirementsBlock } from './CustomerSetupRequirementsBlock';
import { CustomerLaunchReadinessBlock } from './CustomerLaunchReadinessBlock';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { TableToolbar } from './TableToolbar';

const { semantic } = colorTokens;

const moduleGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: '1fr 1fr',
  },
  gap: 1.5,
  px: 2.5,
  pb: 2.5,
};

const moduleCardSx = {
  p: 2,
  minWidth: 0,
  bgcolor: semantic.bg.input,
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.md}px`,
};

const nameSx = {
  fontFamily: typographyTokens.fontFamily,
  fontWeight: typographyTokens.fontWeightSemibold,
  fontSize: 13,
  lineHeight: '20px',
  color: semantic.text.primary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const mutedSx = {
  mt: 0.75,
  fontSize: 12,
  lineHeight: '16px',
  color: semantic.text.muted,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function ModuleCard({ module }) {
  const label = formatModuleLabel(module);
  const sourceLabel = formatModuleSource(module.source);
  const status = module.source || (module.enabled ? 'included' : 'not_subscribed');

  return (
    <Box sx={moduleCardSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          minWidth: 0,
        }}
      >
        <Typography component="span" sx={nameSx}>
          {label}
        </Typography>
        <StatusBadge
          status={status}
          category="generic"
          apiLabel={sourceLabel || undefined}
          size="Sm"
          tone={module.source === 'override' ? 'Warning' : undefined}
          sx={{ alignSelf: 'flex-start', flexShrink: 0 }}
        />
      </Box>
      {module.enabled === false ? (
        <Typography sx={mutedSx}>{formatModuleSource('not_subscribed')}</Typography>
      ) : null}
    </Box>
  );
}

export function CustomerPortalModulesSection() {
  const { t } = useTranslation(['mafertappModules', 'common', 'customerPortal', 'customers']);
  const { activeCustomerId } = useCustomerPortal();
  const [modulesData, setModulesData] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!activeCustomerId) return;
    setLoading(true);
    setError('');
    try {
      const tenantsResponse = await getCustomerPortalTenants(activeCustomerId);
      const tenant = (tenantsResponse.data || tenantsResponse || [])[0];
      if (!tenant?.id) {
        setModulesData(null);
        setTenantId(null);
        return;
      }
      setTenantId(tenant.id);
      const data = await getCustomerPortalTenantModules(activeCustomerId, tenant.id);
      setModulesData(data);
    } catch {
      setError(t('common:load_failed'));
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const paidModules = modulesData?.paid_modules || [];
  const coreModules = modulesData?.core_modules || [];

  const includedModules = useMemo(() => ([
    ...(modulesData?.paid_modules || []),
    ...(modulesData?.support_modules || []),
    ...(modulesData?.future_override_modules || []),
  ]), [modulesData]);

  const recordCount = includedModules.length + coreModules.length;

  if (loading) {
    return <LoadingSkeleton type="CardGrid" rows={4} />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2.5 }}>
        {error}
      </Alert>
    );
  }

  if (!modulesData) {
    return (
      <SectionCard variant="Default" title={t('mafertappModules:my_modules')} sx={{ mb: 0 }}>
        <Typography sx={{ fontSize: 13, lineHeight: '18px', color: semantic.text.muted }}>
          {t('customers:portal.no_active_paid_modules')}
        </Typography>
      </SectionCard>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <SectionCard variant="Default" noPadding sx={{ mb: 2.5, width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('mafertappModules:my_modules')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('customers:portal.my_modules_toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        <Box sx={{ pt: 2 }}>
          {modulesData.global_settings_included && (
            <Box
              sx={{
                mx: 2.5,
                mb: 2,
                p: 2,
                bgcolor: semantic.bg.input,
                border: `1px solid ${semantic.border.subtle}`,
                borderRadius: `${radiusTokens.md}px`,
              }}
            >
              <Typography sx={nameSx}>
                {t('customers:portal.baseline_settings')}
              </Typography>
              <Typography sx={mutedSx}>
                {t('mafertappModules:globalSettingsIncluded')}
              </Typography>
            </Box>
          )}

          {paidModules.length === 0 && (
            <Typography
              sx={{
                px: 2.5,
                mb: 2,
                fontSize: 13,
                lineHeight: '18px',
                color: semantic.text.muted,
              }}
            >
              {t('customers:portal.no_active_paid_modules')}
            </Typography>
          )}

          {includedModules.length > 0 || coreModules.length > 0 ? (
            <Box sx={moduleGridSx}>
              {includedModules.map((mod) => (
                <ModuleCard key={`${mod.module_code}-${mod.source}-included`} module={mod} />
              ))}
              {coreModules.map((mod) => (
                <ModuleCard key={`${mod.module_code}-${mod.source}-core`} module={mod} />
              ))}
            </Box>
          ) : (
            !modulesData.global_settings_included && (
              <Typography
                sx={{
                  px: 2.5,
                  pb: 2.5,
                  fontSize: 13,
                  lineHeight: '18px',
                  color: semantic.text.muted,
                }}
              >
                {t('mafertappModules:no_modules')}
              </Typography>
            )
          )}
        </Box>
      </SectionCard>

      {tenantId && <CustomerSetupRequirementsBlock tenantId={tenantId} />}
      {tenantId && <CustomerLaunchReadinessBlock tenantId={tenantId} />}
    </Box>
  );
}
